import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Maximize2, Loader2, TrendingUp, TrendingDown } from 'lucide-react';
import { apiRequest } from '../../lib/utils';
import { Button } from '../ui/button';

export default function AllStoresWidget({ expandable, expandPath }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stores, setStores] = useState([]);

  useEffect(() => {
    fetchAllStores();
  }, []);

  const fetchAllStores = async () => {
    try {
      const data = await apiRequest('/store-scorecards');
      setStores(data.scorecards || []);
    } catch (error) {
      console.error('Failed to load stores:', error);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600 bg-green-50';
    if (score >= 70) return 'text-amber-600 bg-amber-50';
    return 'text-red-600 bg-red-50';
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-slate-200 p-4 h-full">
        <div className="flex items-center justify-center h-32">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      </div>
    );
  }

  // Sort by score descending
  const sortedStores = [...stores].sort((a, b) => (b.overall_score || 0) - (a.overall_score || 0));
  const topStores = sortedStores.slice(0, 5);

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4 h-full" data-testid="all-stores-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Building2 className="h-5 w-5 text-slate-600" />
          <h3 className="font-semibold text-slate-900">All Stores</h3>
        </div>
        {expandable && expandPath && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(expandPath)}
            className="h-8 w-8 p-0"
          >
            <Maximize2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      {topStores.length > 0 ? (
        <div className="space-y-2">
          {topStores.map((store, index) => (
            <div 
              key={store.store} 
              className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer"
              onClick={() => navigate('/admin/store-leadership')}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                index === 0 ? 'bg-yellow-100 text-yellow-700' :
                index === 1 ? 'bg-slate-200 text-slate-700' :
                index === 2 ? 'bg-orange-100 text-orange-700' :
                'bg-slate-100 text-slate-500'
              }`}>
                {index + 1}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{store.store}</p>
              </div>
              <div className={`px-2 py-1 rounded text-sm font-semibold ${getScoreColor(store.overall_score)}`}>
                {store.overall_score?.toFixed(0) || 0}%
              </div>
              {store.trend !== undefined && (
                store.trend >= 0 
                  ? <TrendingUp className="h-4 w-4 text-green-500" />
                  : <TrendingDown className="h-4 w-4 text-red-500" />
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-4 text-slate-500 text-sm">
          <Building2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p>No store data available</p>
        </div>
      )}

      {stores.length > 5 && (
        <Button 
          variant="ghost" 
          size="sm" 
          className="w-full mt-2"
          onClick={() => navigate('/admin/store-leadership')}
        >
          View all {stores.length} stores
        </Button>
      )}
    </div>
  );
}
